import { Flex, Text, SimpleGrid, useMantineTheme } from "@mantine/core";
import { useTranslation } from "react-i18next";
import InfoCards from "./InfoCards";
import { Chart1, TrendUp } from "iconsax-react";
import { IResponsePerformance } from "../../hooks";

interface IProps {
  dataPerformance: IResponsePerformance;
}
export default function BulletsPerformance({ dataPerformance }: IProps) {
  const theme = useMantineTheme();
  const { t } = useTranslation();

  const parseLocaleNumber = (input: unknown): number => {
    if (typeof input === "number") return input;
    if (input == null) return NaN;
    let s = String(input).trim();
    if (s.length === 0) return NaN;
    s = s.replace(/[%\s]/g, "");
    const hasComma = s.includes(",");
    const hasDot = s.includes(".");
    if (hasComma && hasDot) {
      const lastComma = s.lastIndexOf(",");
      const lastDot = s.lastIndexOf(".");
      if (lastDot > lastComma) {
        return parseFloat(s.replace(/,/g, ""));
      } else {
        return parseFloat(s.replace(/\./g, "").replace(",", "."));
      }
    }
    if (hasComma && !hasDot) {
      return parseFloat(s.replace(/\./g, "").replace(",", "."));
    }
    return parseFloat(s);
  };

  const assertivityPercent = parseLocaleNumber(dataPerformance?.assertivity);

  const isAssertivityPositive = !isNaN(assertivityPercent)
    ? assertivityPercent <= 50
    : undefined;

  const dataBullets = [
    {
      name: "marketplace.detalhe.bullets_performance.profitability",
      value: dataPerformance?.profitability,
      isNegative: dataPerformance?.profitability.includes("-"),
    },
    {
      name: "marketplace.detalhe.bullets_performance.total_gain",
      value: dataPerformance?.total_gain,
      isNegative: dataPerformance?.total_gain.includes("-"),
    },
    {
      name: "marketplace.detalhe.bullets_performance.total_loss",
      value: dataPerformance?.total_loss,
      isNegative: dataPerformance?.total_loss.includes("-"),
    },
    {
      name: "marketplace.detalhe.bullets_performance.total",
      value: dataPerformance?.total,
      isNegative: dataPerformance?.total.includes("-"),
    },
    {
      name: "marketplace.detalhe.bullets_performance.avg_daily",
      value: dataPerformance?.avg_daily,
      isNegative: dataPerformance?.avg_daily.includes("-"),
    },
    {
      name: "marketplace.detalhe.bullets_performance.max_gain",
      value: dataPerformance?.max_gain,
      isNegative: dataPerformance?.max_gain.includes("-"),
    },
    {
      name: "marketplace.detalhe.bullets_performance.max_loss",
      value: dataPerformance?.max_loss,
      isNegative: dataPerformance?.max_loss.includes("-"),
    },

    {
      name: "marketplace.detalhe.bullets_performance.qty_days",
      value: dataPerformance?.qty_days,
    },
    {
      name: "marketplace.detalhe.bullets_performance.qty_gain_days",
      value: dataPerformance?.qty_gain_days,
      isNegative: false,
    },
    {
      name: "marketplace.detalhe.bullets_performance.qty_loss_days",
      value: dataPerformance?.qty_loss_days,
      isNegative: true,
    },
    {
      name: "marketplace.detalhe.bullets_performance.assertivity",
      value: dataPerformance?.assertivity,
      isNegative: isAssertivityPositive,
    },
  ];
  return (
    <Flex direction={"column"} gap="20px" mt="20px">
      <Flex justify="start" align={"center"} gap="8px">
        <TrendUp size="24" color={theme.colors.basic[5]} />
        <Text fz={{ base: 20 }} lh={{ base: "32px" }} c={theme.colors.basic[5]}>
          {t("marketplace.detalhe.bullets_performance.title")}
        </Text>
      </Flex>
      <SimpleGrid
        breakpoints={[
          { minWidth: "100", cols: 1 },
          { minWidth: "800", cols: 2 },
          { minWidth: "1200", cols: 3 },
          { minWidth: "1500", cols: 4 },
        ]}
      >
        {dataBullets.map((item, index) => (
          <InfoCards
            key={index}
            icon={<Chart1 size={24} />}
            name={t(item.name)}
            value={item.value}
            isNegative={item.isNegative}
          />
        ))}
      </SimpleGrid>
    </Flex>
  );
}
